/** Statut d'équipement — jeu fermé de trois valeurs, toujours calculé (RM-PIN-194). */
export const EQUIPMENT_STATUSES = ['Équipé', 'Partiel', 'À équiper'] as const;
export type EquipmentStatus = (typeof EQUIPMENT_STATUSES)[number];

/** Statut RH — jeu fermé de quatre valeurs (RM-PIN-197). */
export const EMPLOYMENT_STATUSES = ['À venir', 'Actif', 'Suspendu', 'Parti'] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

/**
 * Code envoyé au backend pour le statut RH : l'énumération `EmploymentStatus` du module Directory
 * est sérialisée par sa valeur numérique (1 = À venir … 4 = Parti).
 */
export const EMPLOYMENT_STATUS_CODES: Record<EmploymentStatus, 1 | 2 | 3 | 4> = {
  'À venir': 1,
  Actif: 2,
  Suspendu: 3,
  Parti: 4,
};

/**
 * Service (entité organisationnelle) — `Department` en code, jamais `Service` (collision avec la DI).
 * Le rattachement à un service existant est obligatoire (RM-PIN-191) et fonde le périmètre du
 * responsable de service (RM-PIN-195).
 */
export interface Department {
  readonly id: string;
  readonly name: string;
}

/**
 * Fiche d'annuaire telle qu'elle est stockée. Nom et prénom sont conservés séparément comme dans le
 * domaine : ce sont deux champs obligatoires distincts (RM-PIN-191) et le libellé affiché s'en
 * déduit, jamais l'inverse.
 */
export interface Collaborator {
  readonly id: string;
  readonly lastName: string;
  readonly firstName: string;
  /** Identifiant fonctionnel unique de l'annuaire, comparé sans tenir compte de la casse (RM-PIN-190). */
  readonly email: string;
  readonly departmentId: string;
  readonly jobTitle: string;
  readonly employmentStatus: EmploymentStatus;
  /** Format ISO `AAAA-MM-JJ`, celui attendu par le backend. */
  readonly arrivalDate: string;
  /** Manager hiérarchique, saisi dans l'annuaire et faisant foi pour les périmètres (RM-PIN-192). */
  readonly managerId: string | null;
  readonly internalNotes: string | null;
}

/** Ligne de l'écran « Vos employés », avec les compteurs d'appareils par famille. */
export interface CollaboratorRow extends Collaborator {
  /** « Prénom Nom », comme le libellé affiché du domaine. */
  readonly name: string;
  readonly department: string;
  readonly managerName: string | null;
  /** Famille « poste de travail » : PC portable ou PC fixe (RM-PIN-194). */
  readonly computers: number;
  /** Famille « mobile » : smartphones et tablettes. */
  readonly phones: number;
  readonly others: number;
  readonly total: number;
  readonly equipmentStatus: EquipmentStatus;
  readonly initials: string;
  /** RM-PIN-197 — collaborateur parti détenant encore du matériel assigné. */
  readonly departedWithEquipment: boolean;
}

/**
 * Classe de badge de la datatable du kit (`.uni-dt-badge--*`) par statut d'équipement, alignée sur
 * la correspondance de `equipmentVariant` : Équipé = succès, Partiel = avertissement, À équiper =
 * danger. Les variantes « soft » posent un texte 600/700 sur une teinte 50 (contraste AA respecté).
 */
export function equipmentBadgeClass(status: EquipmentStatus): string {
  const variant: Record<EquipmentStatus, string> = {
    Équipé: 'success-soft',
    Partiel: 'warning-soft',
    'À équiper': 'danger-soft',
  };
  return `uni-dt-badge uni-dt-badge--${variant[status]}`;
}

export function equipmentStatusStyle(status: EquipmentStatus): {
  bg: string;
  fg: string;
  border: string;
} {
  const styles: Record<EquipmentStatus, { bg: string; fg: string; border: string }> = {
    Équipé: { bg: 'var(--surface-success-subtle)', fg: 'var(--text-primary)', border: 'var(--border-success)' },
    Partiel: { bg: 'var(--surface-warning-subtle)', fg: 'var(--text-primary)', border: 'var(--border-warning)' },
    'À équiper': { bg: 'var(--surface-danger-subtle)', fg: 'var(--text-primary)', border: 'var(--border-danger)' },
  };
  return styles[status];
}

/** Libellé affiché — « Prénom Nom », comme `PersonName.Display` côté domaine. */
export function displayName(collaborator: Pick<Collaborator, 'firstName' | 'lastName'>): string {
  return `${collaborator.firstName} ${collaborator.lastName}`.trim();
}

/**
 * Contrôle de forme de l'email, identique à celui de l'objet-valeur `ProfessionalEmail` : une seule
 * arobase, du texte de part et d'autre, un point dans le domaine, aucun espace. Valider plus
 * finement qu'ici produit surtout des faux négatifs.
 */
export function isPlausibleEmail(candidate: string): boolean {
  const parts = candidate.split('@');

  return (
    parts.length === 2 &&
    parts[0].length > 0 &&
    parts[1].includes('.') &&
    !parts[1].startsWith('.') &&
    !parts[1].endsWith('.') &&
    !/\s/.test(candidate)
  );
}
